import { StateCreator } from 'zustand';

import { StoreSlices } from '.';
import { getIdFromGid } from '../../../utils/gid-utils';
import {
  BasePropertiesType,
  getPrice,
} from '../../bundles/CreateBundle/useCreateBundle';
import { getItemPropertiesFromState } from '../../bundles/CreateBundle/utils';
import { goBack } from '../../bundles/bundleUtils';
import {
  addBundleToCartAndRedirect,
  getSelectedProductsWithPropsForCart,
} from '../lib/cart';

export interface StepperProps {
  steps: string[];
  currentStep: number;
  isLastStep: boolean;
  isFirstStep: boolean;
  noAddonsCollection: boolean;
  contentStep: string;
}

export interface StepperSlice extends StepperProps {
  setCurrentStep: (step: number) => void;
  handleNext: () => void;
  handlePrev: () => void;
}

export const createStepperSlice =
  (
    initProps?: Partial<StepperProps>,
  ): StateCreator<
    Partial<StoreSlices>,
    [['zustand/devtools', never], ['zustand/immer', never]],
    [],
    StepperSlice
  > =>
  (set, get) => ({
    currentStep: initProps?.currentStep,
    steps: initProps?.steps,
    isLastStep: initProps?.isLastStep,
    isFirstStep: initProps?.isFirstStep,
    noAddonsCollection: initProps?.noAddonsCollection,
    contentStep: initProps?.contentStep,
    setCurrentStep: (step: number) =>
      set(
        (state) => {
          state.stepper.currentStep = step;
        },
        undefined,
        'stepper/setCurrentStep',
      ),
    handleNext: () => {
      const {
        stepper,
        sections: {
          productsInAddonsCollection,
          sellingPlan,
          settings,
          selectedAddons,
          bundle,
          product,
          productBundle,
          variantId,
          size,
          hasDynamicSize,
          minSize,
          maxSize,
          dynamicDiscountCode,
          selectedProducts,
          individualProductsInCollection,
          productsInCollection,
          setIsAddingToCart,
        },
      } = get();

      const bundleId = getIdFromGid(settings.VariantId);
      const step = stepper.currentStep;
      const babProduct = product || productBundle;
      const price = getPrice(babProduct, settings, variantId);
      const isLastStep = step === 3;
      const noAddonsCollection =
        !productsInAddonsCollection || productsInAddonsCollection.length === 0;

      if (isLastStep || noAddonsCollection) {
        let baseProperties: BasePropertiesType = {
          _bundleLabel: settings.Label,
          _bundleProductId: bundle.ProductId,
          _bundleName: bundle.Name,
          _bundleBoxSize: size,
          _variantId: variantId,
          _bundleBoxPrice: price,
        };

        if (hasDynamicSize) {
          baseProperties = {
            ...baseProperties,
            _bundleBoxMinSize: minSize,
            _bundleBoxMaxSize: maxSize,
          };
        }

        const itemProperties = getItemPropertiesFromState(
          baseProperties,
          selectedProducts,
          individualProductsInCollection,
        )();

        const productsWithProps = getSelectedProductsWithPropsForCart({
          handle: bundle.Handle,
          productsInCollection,
          selectedProducts,
          baseProperties,
          isDynamic: hasDynamicSize,
          sellingPlan,
        });

        (async () => {
          try {
            setIsAddingToCart(true);
            await addBundleToCartAndRedirect({
              bundleId,
              sellingPlan,
              itemProperties,
              addons: selectedAddons,
              productsInAddonsCollection,
              productsWithProps,
              discountCode: dynamicDiscountCode,
              isDynamic: bundle.Type === 'dynamicBaB',
            });
          } catch (e) {
            setIsAddingToCart(false);
            console.log(e);
          } finally {
            setTimeout(() => {
              setIsAddingToCart(false);
            }, 1000);
          }
        })();
      } else {
        set(
          (state) => {
            state.stepper.currentStep += 1;
          },
          false,
          'stepper/setCurrentStep',
        );
      }
    },
    handlePrev: () =>
      set(
        (state) => {
          const isFirstStep = get().stepper.currentStep === 2;
          if (isFirstStep) goBack();
          else state.stepper.currentStep = state.stepper.currentStep - 1;
        },
        undefined,
        'stepper/setCurrentStep',
      ),
  });
